import { IsGSASmartPay } from 'smg/cc';
import { DollarFormat } from 'smg/util';

import { PaymentPlan } from './payment/plan';
import { State } from './state';

// SetupSmartPayWarning wires the order-summary nudge for customers paying
// with a GSA SmartPay government card while the order still carries sales
// tax (#3777). The BIN is a hint, not proof (travel cards share the GSA
// prefixes), so this only ever warns — never blocks or auto-exempts.
export function SetupSmartPayWarning(state: State): void {
    state.dom.$gsaSmartPayAddExemption.addEventListener('click', e => {
        e.preventDefault();
        openTaxSection(state);
    });
}

// UpdateSmartPayWarning re-evaluates the warning. updateTotals zeroes
// taxAmount client-side when an applicable exemption is selected, so
// taxAmount > 0 already means "taxed with no exemption".
export function UpdateSmartPayWarning(state: State): void {
    const { dom } = state;
    const show = state.taxAmount.gt(0) && gsaFirst6s(state).length > 0;
    dom.$gsaSmartPayTaxAmount.textContent = DollarFormat(state.taxAmount);
    dom.$gsaSmartPayWarning.classList.toggle('d-none', !show);
}

// Acknowledgment is per-BIN, so swapping to a different GSA card prompts
// again. Module-level: the checkout page is a single session, matching the
// other checkouts' per-page-load acknowledgment.
const acknowledgedFirst6s = new Set<string>();

// ConfirmSmartPaySubmit gates Place Order the same way the other eight
// checkouts do: resolves true to continue this same submission, false when
// the customer declines (the "Add Tax Exemption" choice also reopens the
// tax section). Plus tokenizes at the payment step, so the live summary
// warning has usually already shown — the prompt is the explicit ack.
export async function ConfirmSmartPaySubmit(state: State): Promise<boolean> {
    if (!state.taxAmount.gt(0)) {
        return true;
    }
    const first6s = gsaFirst6s(state);
    if (!first6s.length) {
        return true;
    }
    const gsaSmartPay = await import('smg/gsa-smartpay');
    for (const first6 of first6s) {
        if (acknowledgedFirst6s.has(first6)) {
            continue;
        }
        const choice = await gsaSmartPay.ConfirmGSASmartPay(DollarFormat(state.taxAmount));
        if (choice === 'continue') {
            acknowledgedFirst6s.add(first6);
            continue;
        }
        if (choice === 'exemption') {
            openTaxSection(state);
        }
        return false;
    }
    return true;
}

function gsaFirst6s(state: State): string[] {
    const seen: string[] = [];
    for (const entry of PaymentPlan(state).entries) {
        if (!entry.first6 || !IsGSASmartPay(entry.first6) || seen.includes(entry.first6)) {
            continue;
        }
        seen.push(entry.first6);
    }
    return seen;
}

function openTaxSection(state: State): void {
    const { dom } = state;
    if (dom.$taxBody?.classList.contains('show')) {
        dom.$taxBody.scrollIntoView({ behavior: 'smooth', block: 'start' });
        dom.$taxToggleYes.click();
        return;
    }
    dom.$taxBody?.addEventListener('shown.bs.collapse', () => dom.$taxToggleYes.click(), { once: true });
    dom.$taxBodyToggle?.click();
}
